const SITE_BASE_URL = "https://premierelens.com";

export const extractImdbId = (show) => {
  if (!show) {
    return null;
  }
  const imdbId =
    show.imdbId ||
    show.imdb_id ||
    show.omdbData?.imdbId ||
    show.omdbData?.imdbID ||
    null;
  if (imdbId && typeof imdbId === "string") {
    return imdbId;
  }
  if (show.detailsUrl) {
    const match = show.detailsUrl.match(/tt\d+/);
    return match ? match[0] : null;
  }
  return null;
};

export const extractTvdbId = (show) => {
  if (!show) {
    return null;
  }
  const tvdbId = show.tvdbId || show.tvdb_id || null;
  if (tvdbId === null || tvdbId === undefined) {
    return null;
  }
  const numeric = Number(tvdbId);
  return Number.isNaN(numeric) ? null : numeric;
};

export const extractTmdbId = (show) => {
  if (!show) {
    return null;
  }
  const tmdbId = show.tmdbId || show.tmdb_id || show.themoviedbId || null;
  if (tmdbId === null || tmdbId === undefined) {
    return null;
  }
  const numeric = Number(tmdbId);
  return Number.isNaN(numeric) ? null : numeric;
};

const uniqueValues = (values = []) => [...new Set(values.filter(Boolean))];

const getShowUrl = (show) => {
  if (!show?.slug) {
    return "";
  }
  return `${SITE_BASE_URL}/show/${show.slug}`;
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const text = Array.isArray(value) ? value.join("; ") : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const buildCsvExport = (shows = []) => {
  const headers = [
    "title",
    "year",
    "releaseDate",
    "imdbId",
    "tvdbId",
    "tmdbId",
    "slug",
    "url",
  ];
  const rows = shows.map((show) => [
    show.title || "",
    show.year || "",
    show.releaseDate || "",
    extractImdbId(show) || "",
    extractTvdbId(show) || "",
    extractTmdbId(show) || "",
    show.slug || "",
    getShowUrl(show),
  ]);

  return [headers, ...rows]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
};

export const buildTvdbExport = (shows = []) => {
  const tvdbIds = shows
    .map((show) => extractTvdbId(show))
    .filter((id) => Number.isFinite(id));

  return uniqueValues(tvdbIds);
};

export const buildTraktExport = (shows = []) => {
  const traktShows = shows
    .map((show) => {
      const ids = {
        imdb: extractImdbId(show) || undefined,
        tvdb: extractTvdbId(show) || undefined,
        tmdb: extractTmdbId(show) || undefined,
      };
      const compactIds = Object.fromEntries(
        Object.entries(ids).filter(([, value]) => value !== undefined && value !== null)
      );

      return {
        title: show.title,
        ...(show.year ? { year: show.year } : {}),
        ids: compactIds,
      };
    })
    .filter((show) => show.title && Object.keys(show.ids).length > 0);

  return { shows: traktShows };
};

export const buildImdbExport = (shows = []) => {
  const imdbIds = shows
    .map((show) => extractImdbId(show))
    .filter((id) => typeof id === "string" && id.startsWith("tt"));

  return [...new Set(imdbIds)];
};

export const buildBackupExport = (shows = []) => ({
  version: 2,
  exportedAt: new Date().toISOString(),
  shows,
});

export const buildWatchlistExportPayload = ({ format = "csv", shows = [] } = {}) => {
  if (format === "csv") {
    return buildCsvExport(shows);
  }
  if (format === "imdb") {
    return buildImdbExport(shows);
  }
  if (format === "tvdb") {
    return buildTvdbExport(shows);
  }
  if (format === "trakt") {
    return buildTraktExport(shows);
  }
  if (format === "backup") {
    return buildBackupExport(shows);
  }
  return buildCsvExport(shows);
};

const buildFilename = (format) => {
  const dateStamp = new Date().toISOString().slice(0, 10);
  if (format === "csv") {
    return `premierelens-my-list-${dateStamp}.csv`;
  }
  if (format === "imdb") {
    return `premierelens-my-list-imdb-${dateStamp}.txt`;
  }
  if (format === "tvdb") {
    return `premierelens-my-list-tvdb-${dateStamp}.txt`;
  }
  if (format === "trakt") {
    return `premierelens-my-list-trakt-${dateStamp}.json`;
  }
  if (format === "backup") {
    return `premierelens-my-list-backup-${dateStamp}.json`;
  }
  return `premierelens-my-list-${dateStamp}.csv`;
};

const buildMimeType = (format) => {
  if (format === "csv") {
    return "text/csv";
  }
  if (format === "imdb" || format === "tvdb") {
    return "text/plain";
  }
  return "application/json";
};

export const downloadExportFile = ({ format, payload }) => {
  if (!payload) {
    return;
  }

  const filename = buildFilename(format);
  const mimeType = buildMimeType(format);
  const content =
    format === "csv"
      ? String(payload)
      : format === "imdb" || format === "tvdb"
      ? Array.isArray(payload)
        ? payload.join("\n")
        : String(payload)
      : JSON.stringify(payload, null, 2);

  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const exportWatchlist = ({ format = "csv", shows = [] } = {}) => {
  const payload = buildWatchlistExportPayload({ format, shows });
  downloadExportFile({ format, payload });
};

export const parseBackupImport = (jsonText) => {
  let parsed;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    throw new Error("File is not valid JSON.");
  }

  const shows = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.shows)
      ? parsed.shows
      : null;

  if (!shows) {
    throw new Error("File does not contain a recognisable My List backup.");
  }

  const valid = shows.filter(
    (s) => s && typeof s === "object" && (s.slug || s.id || s.imdbId)
  );

  if (valid.length === 0) {
    throw new Error("No valid shows found in the backup file.");
  }

  return valid;
};
